import type { FormikHelpers } from "formik";
import { Formik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { initialValues, validationSchema, getSchemas } from "./landUseChange";
import { useAppSelector, useThreadsProcessor } from "@app/hooks";
import { selectCurrentProject } from "@features/project/projectSlice";
import {
	useContextAwareGetLandUseChange,
	useUpdateLandUseChangeMutation,
} from "@hooks/index";
import type { ModuleFull } from "./landUseChangeTypes";
import GenericModule from "../GenericModule";
import useModuleHook from "../useModuleHook";
import { instanceOfSWW } from "../inputs/utils";
import useErrorMessage from "@utils/useErrorMessage";
import { selectCurrentBuilder } from "@features/builder/builderSlice";
import { BEModulesClassNames, FEModules } from "@utils/moduleList";

const LandUseChange = () => {
	const { activeActivityId } = useAppSelector(selectCurrentProject);
	const { moduleList } = useAppSelector(selectCurrentBuilder);
	const id = moduleList.find(({ id }) => id === FEModules.LandUseChange)?.uniqueId;

	const {
		data: landUseChangeData,
		isLoading,
		isError,
		error,
	} = useContextAwareGetLandUseChange(id ?? 0, { refetchOnMountOrArgChange: true, skip: !id });
	const [updateLandUseChange, { error: updateError, isError: isUpdateError }] =
		useUpdateLandUseChangeMutation();

	const getSchemaCallback = () => {
		const { moduleSchema, t2Schema } = getSchemas();
		const isWithDisabled =
			landUseChangeData?.module.module_type_start ===
			landUseChangeData?.module.module_type_w;
		const isWithoutDisabled =
			landUseChangeData?.module.module_type_start ===
			landUseChangeData?.module.module_type_wo;

		if (!isWithDisabled && !isWithoutDisabled)
			return { moduleSchema, t2Schema };

		const editedInitInputGroups = moduleSchema.initInputGroups.map(
			(inputGroup) =>
				inputGroup.inputName !== "module.module_type" &&
					instanceOfSWW(inputGroup)
					? {
						...inputGroup,
						withProps: { ...inputGroup.withProps, disabled: isWithDisabled },
						withoutProps: {
							...inputGroup.withoutProps,
							disabled: isWithoutDisabled,
						},
					}
					: inputGroup,
		);

		return {
			moduleSchema: { ...moduleSchema, initInputGroups: editedInitInputGroups },
			t2Schema,
		};
	};

	const { moduleSchema, t2Schema } = useMemo(getSchemaCallback, [
		landUseChangeData,
]);
	const [initValues, setInitValues] = useState<ModuleFull>({
		module: initialValues,
	});

	const { errorMsg } = useModuleHook({
		skeletonsLoadingDeps: [isLoading],
		isMutateError: isUpdateError,
		mutateError: updateError,
	});
	const { errorMsg: blockingError } = useErrorMessage({ isError, error });
	const { processModuleThreads } = useThreadsProcessor<ModuleFull>();

	useEffect(() => {
		if (landUseChangeData) {
			setInitValues(landUseChangeData);
			processModuleThreads({
				module: landUseChangeData.module
			})
		}
	}, [landUseChangeData, processModuleThreads]);

	const handleSubmit = async (
		values: ModuleFull,
		{ resetForm }: FormikHelpers<ModuleFull>,
	) => {
		const module = { ...values.module };
		try {
			values.module.id &&
				await updateLandUseChange({
					data: { ...values, module },
					activityId: activeActivityId ?? 0,
				}).unwrap();
			resetForm({ values });
		} catch (error) {
			console.error(error);
		}
	};

	return (
		<Formik
			validationSchema={validationSchema}
			initialValues={initValues}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			<>
				<GenericModule
					title="project.land_use_change"
					t2Schema={t2Schema}
					moduleSchema={moduleSchema}
					beModuleType={BEModulesClassNames.LandUseChange}
					isLoading={isLoading}
					footerError={errorMsg}
					blockingError={blockingError}
					isError={isUpdateError}
				/>
			</>
		</Formik>
	);
};

export default LandUseChange;
